/**
 * Public landing page at "/". Anonymous visitors get the marketing page below;
 * an authenticated session skips straight through to /queue. Tailwind is used
 * here only (see ../landing.css for why it's scoped + "tw:"-prefixed) — every
 * other route sticks to the plain CSS in index.css.
 *
 * Colors are pinned to the app's own tokens (src/index.css), not Tailwind's
 * stock palette: the badge/CTA/section accents are --brand/--paid/--free,
 * light value first with a `dark:` counterpart at the dark-theme value (see
 * `[data-theme="light"]` vs `:root` in index.css); the Twitch/YouTube
 * sign-in buttons use --twitch/--youtube, which don't change with theme;
 * and the Wordmark component renders the quelby-lockup.svg image directly
 * (see ../components/Wordmark.tsx), so its colors are fixed regardless of theme.
 * `dark:` itself is wired to the same data-theme attribute useTheme() sets
 * (see ../landing.css), so the choice made here carries into the app.
 */
import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion, type Variants } from "framer-motion";
import { useSession } from "../auth/useSession";
import { useTheme, type Theme } from "../hooks/useTheme";
import { apiUrl } from "../lib/api";
import { Wordmark } from "../components/Wordmark";
import { ThemeToggle } from "../components/ThemeToggle";
import "../landing.css";

const fadeInUp: Variants = {
  hidden: { y: 40, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const cardHover = {
  initial: { y: 0, boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" },
  hover: { y: -8, boxShadow: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)" },
};

// Badge tints keyed to the app's own BRAND/FREE/PAID tokens — same 3 colors
// as the logo's stacked bars, in the same top-to-bottom order.
const BADGE = {
  brand: "tw:bg-[#e06d10]/10 tw:text-[#e06d10] tw:dark:bg-[#fb923c]/10 tw:dark:text-[#fb923c]",
  free: "tw:bg-[#0f9d63]/10 tw:text-[#0f9d63] tw:dark:bg-[#22c55e]/10 tw:dark:text-[#22c55e]",
  paid: "tw:bg-[#7c3aed]/10 tw:text-[#7c3aed] tw:dark:bg-[#8b5cf6]/10 tw:dark:text-[#8b5cf6]",
} as const;

const DECK: { title: string; desc: string; icon: string; badge: keyof typeof BADGE }[] = [
  {
    title: "The Match",
    desc: "Donations and requests are reconciled automatically, the moment both sides land — in either order.",
    icon: "🔗",
    badge: "brand",
  },
  {
    title: "The Queue",
    desc: "One ordered list across Twitch and YouTube. Paid, then free, then completed — always clear.",
    icon: "📋",
    badge: "free",
  },
  {
    title: "The Access",
    desc: "You vouch mods in by hand, one at a time. Nothing is public except the overlay.",
    icon: "🔑",
    badge: "paid",
  },
];

const STEPS = [
  "01. Viewer drops a request or a donation, in either chat",
  "02. quelby matches the donation to the request the instant both exist",
  "03. The queue orders itself — paid, then free, then completed",
  "04. You and your mods manage it live — reorder, pin, skip, complete",
  "05. The OBS overlay updates instantly for viewers",
];

function Landing({ theme, toggleTheme }: { theme: Theme; toggleTheme: () => void }) {
  const stepHoverBg = theme === "dark" ? "#2a2f3a" : "#ffffff";

  return (
    <div className="tw:bg-white tw:dark:bg-[#0f1115] tw:text-neutral-800 tw:dark:text-[#e6e8ee] tw:font-sans tw:selection:bg-[#e06d10]/10 tw:dark:selection:bg-[#fb923c]/10">
      <div className="tw:fixed tw:top-6 tw:inset-x-6 tw:z-50 tw:flex tw:justify-between tw:items-center">
        <div className="tw:bg-white tw:dark:bg-[#171a21] tw:border tw:border-transparent tw:dark:border-[#2a2f3a] tw:pl-4 tw:pr-5 tw:py-2 tw:rounded-full tw:shadow-md">
          <Wordmark size="2.25rem" />
        </div>
        <div className="tw:flex tw:items-center tw:gap-3">
          <ThemeToggle theme={theme} onToggle={toggleTheme} />
          <Link
            to="/login"
            className="tw:no-underline tw:bg-[#e06d10] tw:dark:bg-[#fb923c] tw:text-white tw:dark:text-[#431407] tw:px-5 tw:py-2 tw:rounded-full tw:font-medium tw:shadow-md tw:hover:brightness-90 tw:transition-[filter]"
          >
            Sign in
          </Link>
        </div>
      </div>

      {/* 1. THE HERO (Orientation) */}
      {/* pt-28 + items-start on mobile reserves room below the fixed header
          (~24px offset + ~44px tall toggle button) so a tall card never
          scrolls up under the logo/sign-in pill; sm+ reverts to true
          vertical centering since there's headroom to spare there. */}
      <section className="tw:min-h-screen tw:flex tw:items-start tw:sm:items-center tw:justify-center tw:pt-28 tw:sm:pt-6 tw:px-6 tw:pb-6 tw:relative tw:overflow-hidden">
        <video
          className="tw:absolute tw:inset-0 tw:w-full tw:h-full tw:object-cover"
          autoPlay
          muted
          loop
          playsInline
          poster="/video/hero-poster.jpg"
        >
          <source src="/video/hero-loop.webm" type="video/webm" />
          <source src="/video/hero-loop.mp4" type="video/mp4" />
        </video>
        {/* Scrim: washes the loop with the page's own bg color so the hero
            card (already opaque) stays exactly as legible, and the loop
            reads as ambient motion rather than competing for attention. */}
        <div className="tw:absolute tw:inset-0 tw:bg-white/75 tw:dark:bg-[#0f1115]/80" />

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="tw:bg-white tw:dark:bg-[#1e222b] tw:p-12 tw:md:p-20 tw:rounded-3xl tw:shadow-xl tw:max-w-4xl tw:w-full tw:border tw:border-neutral-100 tw:dark:border-[#2a2f3a] tw:relative tw:z-10"
        >
          <span className="tw:text-[#e06d10] tw:dark:text-[#fb923c] tw:font-bold tw:tracking-widest tw:uppercase tw:text-sm tw:mb-4 tw:block">
            Welcome to quelby
          </span>
          <h1 className="tw:text-5xl tw:md:text-7xl tw:font-medium tw:tracking-tight tw:leading-tight tw:mb-8">
            A calmer stream, <br />
            <span className="tw:text-neutral-400 tw:dark:text-[#6b7280] tw:font-light tw:italic">
              perfectly queued.
            </span>
          </h1>
          <p className="tw:text-xl tw:text-neutral-600 tw:dark:text-[#949aa8] tw:max-w-2xl tw:leading-relaxed tw:mb-10">
            quelby is a donation and media-request queue for live streamers broadcasting to Twitch
            and YouTube. Viewers donate and request songs or videos right in chat; quelby merges
            both into one queue that you and your mods manage live — no spreadsheets, no second
            window.
          </p>
          <Link to="/login" className="tw:no-underline tw:inline-block">
            <motion.span
              whileTap={{ scale: 0.98 }}
              className="tw:inline-block tw:bg-[#e06d10] tw:dark:bg-[#fb923c] tw:text-white tw:dark:text-[#431407] tw:px-8 tw:py-4 tw:rounded-full tw:font-medium tw:shadow-lg tw:hover:brightness-90 tw:transition-[filter]"
            >
              Start Your Queue
            </motion.span>
          </Link>
        </motion.div>
      </section>

      {/* 2. THE DECK (The Offering) */}
      <section className="tw:py-32 tw:px-6 tw:bg-neutral-50 tw:dark:bg-[#171a21]">
        <div className="tw:max-w-6xl tw:mx-auto tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-8">
          {DECK.map((item) => (
            <motion.div
              key={item.title}
              variants={cardHover}
              initial="initial"
              whileHover="hover"
              className="tw:bg-white tw:dark:bg-[#1e222b] tw:p-10 tw:rounded-2xl tw:border tw:border-neutral-100 tw:dark:border-[#2a2f3a] tw:flex tw:flex-col tw:items-start tw:cursor-default"
            >
              <div
                className={`tw:w-14 tw:h-14 tw:rounded-full tw:flex tw:items-center tw:justify-center tw:text-2xl tw:mb-6 ${BADGE[item.badge]}`}
              >
                {item.icon}
              </div>
              <h3 className="tw:text-2xl tw:font-semibold tw:mb-4">{item.title}</h3>
              <p className="tw:text-neutral-500 tw:dark:text-[#949aa8] tw:leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 3. CONTAINMENT (The Process) */}
      <section className="tw:py-32 tw:px-6 tw:bg-white tw:dark:bg-[#0f1115]">
        <div className="tw:max-w-4xl tw:mx-auto">
          <h2 className="tw:text-3xl tw:font-medium tw:mb-12 tw:text-center tw:text-neutral-400 tw:dark:text-[#6b7280]">
            The quelby Way
          </h2>
          <div className="tw:bg-neutral-100 tw:dark:bg-[#1e222b] tw:rounded-[2rem] tw:p-4 tw:shadow-inner">
            {STEPS.map((step) => (
              <motion.div
                key={step}
                whileHover={{ x: 10, backgroundColor: stepHoverBg, scale: 1.01 }}
                className="tw:bg-transparent tw:p-6 tw:mb-2 tw:last:mb-0 tw:rounded-2xl tw:transition-all tw:duration-200 tw:cursor-pointer tw:flex tw:justify-between tw:items-center tw:group"
              >
                <span className="tw:text-lg tw:font-medium tw:text-neutral-700 tw:dark:text-[#e6e8ee]">{step}</span>
                <span className="tw:opacity-0 tw:group-hover:opacity-100 tw:text-[#e06d10] tw:dark:text-[#fb923c] tw:font-bold tw:transition-opacity">
                  &rarr;
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. THE ASCENT (CTA) */}
      <section className="tw:py-40 tw:px-6 tw:bg-[#e06d10] tw:dark:bg-[#fb923c] tw:relative tw:overflow-hidden">
        <div className="tw:max-w-2xl tw:mx-auto tw:text-center tw:relative tw:z-10">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="tw:bg-white tw:dark:bg-[#1e222b] tw:p-16 tw:rounded-[3rem] tw:shadow-2xl"
          >
            <h2 className="tw:text-4xl tw:font-bold tw:mb-6">Ready to clear the queue?</h2>
            <p className="tw:text-neutral-500 tw:dark:text-[#949aa8] tw:text-lg tw:mb-10">
              Trade two chat windows and two donation feeds for one queue you actually control.
            </p>
            <div className="tw:flex tw:flex-col tw:md:flex-row tw:gap-4 tw:justify-center">
              <a
                href={apiUrl("/auth/twitch")}
                className="tw:no-underline tw:bg-[#9146ff] tw:text-white tw:px-10 tw:py-4 tw:rounded-full tw:font-bold tw:shadow-md tw:hover:brightness-90 tw:transition-[filter]"
              >
                Continue with Twitch
              </a>
              <a
                href={apiUrl("/auth/youtube")}
                className="tw:no-underline tw:bg-[#ff0033] tw:text-white tw:px-10 tw:py-4 tw:rounded-full tw:font-bold tw:shadow-md tw:hover:brightness-90 tw:transition-[filter]"
              >
                Continue with YouTube
              </a>
            </div>
            <p className="tw:text-sm tw:text-neutral-400 tw:dark:text-[#6b7280] tw:mt-8 tw:max-w-lg tw:mx-auto">
              Signing in with Twitch or YouTube lets quelby read that channel&apos;s live chat — to
              catch request commands and donation-bot messages and turn them into your queue — and,
              for YouTube, reply with a viewer&apos;s queue position. We only request the access
              needed to run your queue; see our{" "}
              <Link to="/privacy" className="tw:underline tw:hover:text-neutral-600 tw:dark:hover:text-[#949aa8]">
                Privacy Policy
              </Link>{" "}
              for details.
            </p>
          </motion.div>
        </div>

        <div
          className="tw:absolute tw:bottom-0 tw:left-0 tw:w-full tw:h-24 tw:bg-[#e06d10] tw:dark:bg-[#fb923c] tw:skew-y-2 tw:translate-y-12"
          style={{ filter: "brightness(0.85)" }}
        />
      </section>

      <footer className="tw:py-12 tw:text-center tw:text-neutral-400 tw:dark:text-[#6b7280] tw:bg-white tw:dark:bg-[#0f1115] tw:border-t tw:border-neutral-100 tw:dark:border-[#2a2f3a]">
        <p className="tw:text-sm tw:font-medium tw:tracking-widest tw:uppercase tw:flex tw:items-center tw:justify-center tw:gap-2">
          <Wordmark size="0.875rem" /> &copy; 2026 &mdash; One Queue, Both Platforms.
        </p>
        <p className="tw:text-sm tw:mt-4 tw:flex tw:items-center tw:justify-center tw:gap-4">
          <Link to="/privacy" className="tw:no-underline tw:hover:underline tw:hover:text-neutral-600 tw:dark:hover:text-[#949aa8]">
            Privacy Policy
          </Link>
          <Link to="/terms" className="tw:no-underline tw:hover:underline tw:hover:text-neutral-600 tw:dark:hover:text-[#949aa8]">
            Terms of Service
          </Link>
        </p>
      </footer>
    </div>
  );
}

export function Home() {
  const session = useSession();
  const [theme, toggleTheme] = useTheme();
  const [showLoading, setShowLoading] = useState(false);

  // Avoid a loading flash for the common case (session check resolves fast);
  // only render anything for it if the check is actually taking a moment.
  useEffect(() => {
    if (session.status !== "loading") return;
    const timer = setTimeout(() => setShowLoading(true), 150);
    return () => clearTimeout(timer);
  }, [session.status]);

  if (session.status === "authenticated") {
    return <Navigate to="/queue" replace />;
  }
  if (session.status === "loading") {
    if (!showLoading) return null;
    return <div className={theme === "dark" ? "tw:bg-[#0f1115] tw:min-h-screen" : "tw:bg-white tw:min-h-screen"} />;
  }
  return <Landing theme={theme} toggleTheme={toggleTheme} />;
}
